import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useFormikContext } from 'formik';
import AsyncSelect from 'react-select/async';
import { FormGroup } from 'react-bootstrap';
import { gqlGetPostalcode2 } from '../../pages/postalcodes2/graphql';
import { Postalcode2 } from '../../pages/postalcodes2/graphql';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import { isFotokiklosi } from 'shared-pages/common/constants';

type OptionType = {
  value: number;
  label: string;
  data: Postalcode2;
};

const LoadingIndicator = () => (
  <Spinner animation="border" size="sm" className="m-1" variant="success" />
);

const formatOptionLabel = (option: OptionType) => {
  const { data } = option;
  return (
    <div>
      <strong>{data.postalcode}</strong> - {data.area}, {data.city}
      {data.county && <small className="text-muted ms-2">({data.county})</small>}
    </div>
  );
};

interface Props {
  name?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  mTop?: string | number;
  initialData?: any;
}

export const PostalCodeAutocomplete: React.FC<Props> = ({
  name = 'postalCode',
  label,
  placeholder = 'Αναζήτηση Ταχυδρομικού Κώδικα...',
  disabled = false,
  required = false,
  mTop = '0.0rem',
  initialData,
}) => {
  const { t } = useTranslation();
  const formik = useFormikContext<any>();
  const [getPostalCodes, { loading }] = useLazyQuery(gqlGetPostalcode2);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Set initial value when component mounts with existing data
  useEffect(() => {
    if (initialData && initialData.postalCode && !selectedOption) {
      // Create option from initial data
      const initialOption: OptionType = {
        value: 0, // We don't have the postal code ID, but it's not needed for display
        label: `${initialData.postalCode}${
          initialData.addressArea || initialData.addressCity
            ? ` - ${initialData.addressArea || ''}${
                initialData.addressArea && initialData.addressCity ? ', ' : ''
              }${initialData.addressCity || ''}`
            : ''
        }`,
        data: {
          id: 0,
          postalcode: initialData.postalCode,
          area: initialData.addressArea || null,
          city: initialData.addressCity || null,
          county: initialData.addressNomos || null,
          country: null,
          regionId: initialData.regionId || null,
          isActive: true,
          firstDate: null,
          lastDate: null,
          firstUser: null,
          lastUser: null,
          region: null,
        },
      };
      setSelectedOption(initialOption);
    }
  }, [initialData, selectedOption]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    (inputValue: string): Promise<any[]> => {
      return new Promise((resolve) => {
        // Clear existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Set new timeout
        debounceTimeoutRef.current = setTimeout(async () => {
          if (!inputValue || inputValue.length < 3) {
            resolve([]);
            return;
          }

          try {
            const where = JSON.stringify({
              $or: [
                { postalcode: { $like: `%${inputValue}%` } },
                { area: { $like: `%${inputValue}%` } },
                { city: { $like: `%${inputValue}%` } },
              ],
              isActive: true,
            });

            const { data } = await getPostalCodes({
              variables: {
                limit: 50,
                where,
                order: 'postalcode ASC',
              },
            });

            if (data?.getPostalcode2Fotokiklosi?.data) {
              const options = data.getPostalcode2Fotokiklosi.data.map((item: Postalcode2) => ({
                value: item.id,
                label: `${item.postalcode} - ${item.area}, ${item.city}`,
                data: item,
              }));
              resolve(options);
            } else {
              resolve([]);
            }
          } catch (error) {
            console.error('Error loading postal codes:', error);
            resolve([]);
          }
        }, 300); // 300ms debounce delay
      });
    },
    [getPostalCodes],
  );

  const loadOptions = useCallback(
    (inputValue: string) => {
      return debouncedSearch(inputValue);
    },
    [debouncedSearch],
  );

  const handleChange = useCallback(
    (option: OptionType | null) => {
      setSelectedOption(option);

      if (option?.data) {
        const { data: postalData } = option;

        // Set postal code
        formik.setFieldValue(name, postalData.postalcode);

        // Auto-fill address fields
        formik.setFieldValue('addressArea', postalData.area || '');
        formik.setFieldValue('addressCity', postalData.city || '');
        formik.setFieldValue('addressNomos', postalData.county || '');

        // If region is available, set it
        if (postalData.regionId) {
          formik.setFieldValue('regionId', postalData.regionId);
        }
      } else {
        // Clear the postal code field when no option is selected
        formik.setFieldValue(name, '');
        setInputValue(''); // Clear the input value state as well

        // If in edit mode with initial data, restore original values
        if (initialData) {
          formik.setFieldValue('addressArea', initialData.addressArea || '');
          formik.setFieldValue('addressCity', initialData.addressCity || '');
          formik.setFieldValue('addressNomos', initialData.addressNomos || '');
          if (initialData.regionId) {
            formik.setFieldValue('regionId', initialData.regionId);
          }
        } else {
          // If in new mode, clear all related fields
          formik.setFieldValue('addressArea', '');
          formik.setFieldValue('addressCity', '');
          formik.setFieldValue('addressNomos', '');
        }
      }
    },
    [formik, name, initialData],
  );

  const customStyles = useMemo(
    () => ({
      container: (base: any, state: any) => ({
        ...base,
        flex: 1,
        minHeight: '1px',
      }),
      control: (base: any, state: any) => ({
        ...base,
        height: 'calc(1em + 0.5rem + 2px)',
        minHeight: 'calc(1em + 0.5rem + 2px)',
        backgroundColor: disabled ? '#e9ecef' : required ? 'lightYellow' : '#fff',
        borderWidth: '1px',
        boxShadow: !state.isFocused ? 'inset 0 1px 1px #00000013' : '0 0 0 .2rem #007bff40',
        borderColor: '#ced4da',
        '&:hover': {
          borderColor: '#ced4da',
        },
      }),
      input: (base: any, state: any) => ({
        ...base,
        minHeight: '1px',
        marginTop: '0px',
        paddingTop: '0px',
        paddingBottom: '0px',
      }),
      indicatorsContainer: (base: any, state: any) => ({
        ...base,
        height: '25px',
        minHeight: '1px',
      }),
      dropdownIndicator: (base: any, state: any) => ({
        ...base,
        paddingTop: '0',
        paddingBottom: '0',
        minHeight: '1px',
      }),
      indicatorSeparator: (base: any, state: any) => ({
        ...base,
        minHeight: '1px',
        height: '23px',
        marginTop: '0px',
        marginBottom: '0px',
      }),
      clearIndicator: (base: any, state: any) => ({
        ...base,
        paddingTop: '0',
        paddingBottom: '0',
        minHeight: '1px',
      }),
      valueContainer: (base: any, state: any) => ({
        ...base,
        minHeight: '1px',
        height: '25px',
        paddingTop: '0',
        paddingBottom: '0',
        paddingLeft: '8px',
        paddingRight: '8px',
      }),
      singleValue: (base: any, state: any) => ({
        ...base,
        color: '#495057',
        minHeight: '1px',
        paddingTop: '1',
        paddingBottom: '1',
      }),
      loadingIndicator: (base: any, state: any) => ({
        ...base,
        paddingTop: '1',
        paddingBottom: '1',
        minHeight: '1px',
        color: '#007bff80',
      }),
      noOptionsMessage: (base: any, state: any) => ({
        ...base,
        color: '#dc3545',
      }),
      menu: (base: any, state: any) => ({
        ...base,
        zIndex: 3,
        marginTop: '0px',
        borderTopLeftRadius: '0px',
        borderTopRightRadius: '0px',
      }),
      option: (base: any, state: any) => ({
        ...base,
        paddingTop: '4px',
        paddingBottom: '4px',
      }),
    }),
    [disabled, required],
  );

  const displayLabel = label || t('T.K. :');

  // Only show this component for Fotokiklosi
  if (!isFotokiklosi) {
    return null;
  }

  return (
    <FormGroup style={{ marginTop: mTop, position: 'relative', marginBottom: '0.25rem' }}>
      <label htmlFor={name}>
        {displayLabel}
        {required && <span className="text-danger"> *</span>}
      </label>
      <AsyncSelect
        id={name}
        cacheOptions
        loadOptions={loadOptions}
        value={selectedOption}
        onChange={handleChange}
        onInputChange={(value) => setInputValue(value)}
        placeholder={placeholder}
        isDisabled={disabled}
        isSearchable={true}
        isClearable={true}
        isLoading={loading}
        loadingMessage={() => null}
        noOptionsMessage={() => null}
        formatOptionLabel={formatOptionLabel}
        components={{
          LoadingIndicator,
        }}
        styles={customStyles}
        menuPlacement="auto"
        maxMenuHeight={300}
        minMenuHeight={140}
      />
      {inputValue.length > 0 && inputValue.length < 3 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            right: '0',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '0.75rem',
            color: '#6c757d',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginTop: '2px',
            opacity: inputValue.length > 0 && inputValue.length < 3 ? 1 : 0,
            transform:
              inputValue.length > 0 && inputValue.length < 3 ? 'translateY(0)' : 'translateY(-4px)',
            transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: '0',
              height: '0',
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '10px solid #dc3545',
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: '2px',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                fontSize: '8px',
                fontWeight: 'bold',
                lineHeight: '1',
              }}
            >
              !
            </span>
          </span>
          {t('Πληκτρολογήστε τουλάχιστον 3 χαρακτήρες για αναζήτηση')}
        </div>
      )}
    </FormGroup>
  );
};

